import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {StatisticsApiService} from '../services/api/statistics/statistics-api.service';
import {ChartParentType} from '../statistics/enums/chart-parent-type.enum';
import {ChartType} from '../utils/highchart/chart-type';
import {HighchartComponent} from '../utils/highchart/highchart.component';
import {ReportIdentifier} from '../statistics/enums/report-identifier.enum';
import {DateType} from '../statistics/enums/date-type.enum';
import {DateTimeUnitTypes} from '../statistics/enums/date-time-unit-types.enum';
import {ReportFilterType} from '../statistics/enums/report-filter-type.enum';
import {StatisticsTypes} from '../statistics/enums/statistics-types.enum';
import {StatisticsTypesList} from '../statistics/enums/statistics.constants';
import {Router} from '@angular/router';
import {UsersApiService} from '../services/api/users/users/users-api.service';
import {NodeApiService} from '../services/api/network/online/node/node-api.service';
import {RoutingServersSettingsApiService} from '../services/api/routing-servers/routing-servers-settings-api.service';
import {AlarmsApiService} from '../services/api/alarms/alarms-api.service';
import {timer} from 'rxjs';
import * as _ from 'lodash';
import dayjs from 'dayjs';

@Component({
  selector: 'dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss']
})
export class DashboardComponent implements OnInit, OnDestroy {

  routingAttemptsChartOptions: any;
  topRoutingChartOptions: any;
  chartParentType: any;
  chartType: any;
  chartIsStack: any;
  parameters;
  routingAttemptsStatistics;
  activeAlarmsCount;
  autoRefreshSubscription;

  routingAttemptsData = {
    title: 'Routing Attempts',
    subTitle: 'Last 3H',
    value: 0
  };

  nodesData = {
    title: 'ARM Nodes',
    value: 0
  };

  usersData = {
    title: 'Users',
    value: 0
  };

  routersData = {
    title: 'Routers',
    value: 0,
    good: null,
    bad: null
  };

  @ViewChild('routingAttemptsChart', {static: false}) routingAttemptsChart: HighchartComponent;
  @ViewChild('topRoutingChart', {static: false}) topRoutingChart: HighchartComponent;

  constructor(private routingServersSettingsApiService: RoutingServersSettingsApiService,
              private statisticsApiService: StatisticsApiService,
              private statisticsApi: StatisticsApiService,
              private usersApiService: UsersApiService,
              private nodeApiService: NodeApiService,
              private alarmsApiService: AlarmsApiService,
              private router: Router) {
  }

  ngOnInit(): void {
    this.registerAutoRefresh();
    this.initializeData();
  }

  private getChartsData(): void {
    this.statisticsApi.getReportForChart(this.parameters)
      .then((reports) => {
        this.setRoutingAttemptsReport(reports);
      })
      .catch(() => {
      });
  }

  private setRoutingAttemptsReport(value): void {
    let series = value.series.filter((element) => {
      return this.routingAttemptsStatistics.indexOf(element.name) !== -1;
    });

    if (_.isEmpty(series)) {
      series = this.generateEmptySeries();
    }

    this.routingAttemptsChartOptions.series = series;
    this.routingAttemptsChartOptions.startDate = value.startDate || dayjs().subtract(3, 'hours');
    this.routingAttemptsChartOptions.interval = value.interval;

    this.routingAttemptsChart.setOptions(this.routingAttemptsChartOptions);

    this.calculateTotalRoutingAttempts(series[0].data);
  }

  private getRoutingAttemptsStatisticsNames(): any {
    return StatisticsTypesList.filter((element) => {
      return element.value === StatisticsTypes.ROUTING_ATTEMPTS;
    }).map((element) => {
      return element.name;
    });
  }

  navigateToRoutingAttempts(): void {
    this.router.navigate(['/statistics/graphs', {
      statistic: `armOverTime`
    }]);
  }

  navigateToTopRouting(): void {
    this.router.navigate(['/statistics/graphs', {
      statistic: `topRoutingRules`
    }]);
  }

  navigateToActiveAlarms(): void {
    this.router.navigate(['/alarms/active']);
  }

  navigateToNetwork(): void {
    this.router.navigate(['/network/map']);
  }

  private initializeUsers(): void {
    this.usersApiService.getUsersCount().then(totalUsers => {
      this.usersData.value = totalUsers;
    });
  }

  private initializeNodes(): void {
    const params = {
      limit: 1,
      page: 0
    };
    this.nodeApiService.getNodes(params).then((data) => {
      this.nodesData.value = data.totalElements;
    });
  }

  private initializeRouters(): void {
    this.routingServersSettingsApiService.getRoutingServers().subscribe(data => {
      this.routersData.value = data.length;

      this.routersData.good = data.filter(item => item.status === 'AVAILABLE').length;
      this.routersData.bad = this.routersData.value - this.routersData.good;
    });
  }

  private initializeTopRoutingRules(): void {
    this.topRoutingChartOptions = {
      parentType: ChartParentType.COLUMN,
      type: ChartType[ChartParentType.COLUMN].type,
      stacked: false,
      series: [],
      title: {
        text: ''
      },
      limit: null,
      animation: true,
      exporting: false
    };

    const filter = {
      reportIdentifier: 'topRoutingRules',
      dateType: 'RELATIVE',
      relativeTimeUnit: 'HOUR',
      relativeTimeUnitValue: 3,
      numberOfElements: 5,
      statistics: [
        'ROUTING_RULE_ACTION_TRY'
      ]
    };

    this.statisticsApiService.getReport(filter).then(data => {
      this.updateChartParameters(data);
    });
  }

  private initializeRoutingAttempts(): void {
    this.parameters = {
      reportIdentifier: ReportIdentifier.armOverTime,
      dateType: DateType.RELATIVE,
      relativeTimeUnit: DateTimeUnitTypes.HOUR,
      relativeTimeUnitValue: 3,
      availableFilters: [
        ReportFilterType.DATE,
        ReportFilterType.MULTIPLE_STATISTICS
      ],
      multipleStatistics: [
        StatisticsTypes.ROUTING_ATTEMPTS,
        StatisticsTypes.UNSUCCESSFUL_ROUTES,
        StatisticsTypes.ALTERNATIVE_ATTEMPTS,
        StatisticsTypes.DESTINATION_NOT_ROUTABLE,
        StatisticsTypes.DESTINATION_CALL,
        StatisticsTypes.TRANSIENT_CALL
      ]
    };

    this.chartParentType = ChartParentType.LINE;
    this.chartType = ChartType[ChartParentType.LINE].type;
    this.chartIsStack = ChartType[ChartParentType.LINE].stack;

    this.routingAttemptsChartOptions = {
      parentType: this.chartParentType,
      type: this.chartType,
      stacked: this.chartIsStack,
      series: [],
      title: {
        text: ''
      },
      subTitle: '',
      interval: 0,
      animation: true,
      exporting: false
    };

    this.routingAttemptsStatistics = this.getRoutingAttemptsStatisticsNames();

    this.getChartsData();
  }

  private updateChartParameters(parameters): void {
    if (_.isEmpty(parameters.data.series)) {
      return;
    }
    const series = [{
      name: 'Routing rules attempts',
      borderRadius: 6,
      pointWidth: 20,
      data: parameters.data.series[0].data
    }];

    this.topRoutingChartOptions.series = series;

    setTimeout(() => {
      this.topRoutingChart.setOptions(this.topRoutingChartOptions);
    }, 0);
  }

  private initializeActiveAlarms(): void {
    this.alarmsApiService.getActiveAlarmsCount().subscribe(data => {
      this.activeAlarmsCount = data.filter(alarm => alarm.type !== 'cleared');
    });
  }

  private registerAutoRefresh(): void {
    const timeInterval = 30 * 1000;
    this.autoRefreshSubscription = timer(timeInterval, timeInterval).subscribe(() => {
      this.initializeData();
    });
  }

  private initializeData(): void {
    this.initializeUsers();
    this.initializeNodes();
    this.initializeRouters();

    this.initializeRoutingAttempts();
    this.initializeTopRoutingRules();

    this.initializeActiveAlarms();
  }

  ngOnDestroy(): void {
    this.autoRefreshSubscription.unsubscribe();
  }

  private calculateTotalRoutingAttempts(data): void{
    this.routingAttemptsData.value = data.reduce((first: any, second: any) => {
      second = second.y | 0;
      return first + second;
    }, 0);
  }

  private generateEmptySeries(): any {
    const defaultSeries = [
      {
        name: 'Routing attempts',
        data: [
          {
            y: null,
            additionalInfo: null
          }
        ]
      }
    ];

    for (let i = 0; i < 35; i++) {
      defaultSeries[0].data.push({
        y: null,
        additionalInfo: null
      });
    }

    return defaultSeries;
  }
}
